import { z } from 'zod';
import { CONTENT_VERSION } from '../content/lessons';
import { STORED_FIELDS } from './runRecord';
import type { PersistedData } from './types';

/** Namespaced, versioned storage key. Content version is tracked separately inside the envelope. */
export const STORAGE_KEY = 'robotics-control-tutorial/v1';
export const SCHEMA_VERSION = 1;
export const MAX_RUNS = 40;

// ── Validation schemas ───────────────────────────────────────────────
const num = z.number().finite();
const schedule = z.object({ initial: num, steps: z.array(z.object({ t: num, value: num })) });
const estimated = z.object({ m: num, g: num, k: num, zs: num });
const controller = z.object({
  kind: z.enum(['manual', 'open-loop', 'bang-bang', 'p', 'pi', 'pid']),
  ffMode: z.enum(['off', 'operating-point', 'target-equilibrium']),
  estimated: estimated.nullable(),
  Kp: num,
  Ki: num,
  Kd: num,
  antiWindup: z.boolean(),
  derivTau: num,
  bangDelta: num,
  hysteresis: num,
  bangInitial: z.enum(['high', 'low']),
  manualInput: num,
});
export const configSchema = z.object({
  plant: z.object({ m: num, g: num, k: num, b: num, zs: num, Tmax: num }),
  z0: num,
  initial: z.object({ z: num, v: num }),
  reference: schedule,
  disturbance: schedule,
  controller,
  noise: z.object({ enabled: z.boolean(), sigma: num, seed: num }),
  duration: num,
  dt: num,
});
const trace = z.object(Object.fromEntries(STORED_FIELDS.map((f) => [f, z.array(num)])) as Record<(typeof STORED_FIELDS)[number], z.ZodArray<z.ZodNumber>>);
export const runSchema = z.object({
  id: z.string(),
  number: z.number().int(),
  createdAt: num,
  presetId: z.string(),
  presetVersion: z.number().int(),
  labStepId: z.string().nullable(),
  config: configSchema,
  manualEvents: z.array(z.object({ step: z.number().int(), value: num })),
  status: z.enum(['complete', 'stopped']),
  stopReason: z.object({ code: z.enum(['contact', 'ceiling', 'nonfinite', 'invalid-input']), t: num, message: z.string() }).nullable(),
  stats: z.object({ saturationTime: num, iae: num, switchCount: num, maxAltitude: num, minAltitude: num }),
  trace,
  engine: z.string(),
});
const answer = z.union([num, z.string(), z.record(z.string(), z.string()), z.array(z.string()), z.null()]);
const gateRecord = z.object({
  gateId: z.string(),
  gateVersion: z.number().int(),
  attempts: z.number().int(),
  items: z.record(
    z.string(),
    z.object({ qVersion: z.number().int(), runIds: z.record(z.string(), z.string()), answer, correct: z.boolean(), attempts: z.number().int() }),
  ),
  passed: z.boolean(),
  passedAt: num.nullable(),
  evidence: z.record(z.string(), z.string()).nullable(),
  summary: z.string().nullable(),
});
export const dataSchema = z.object({
  currentLessonId: z.string(),
  runCounter: z.number().int(),
  runs: z.array(runSchema),
  progress: z.object({ evidence: z.record(z.string(), z.string()), gates: z.record(z.string(), gateRecord) }),
  predictions: z.record(z.string(), z.string()),
  reflections: z.record(z.string(), z.string()),
  comparison: z.array(z.string()).max(3),
  experiment: z.object({ presetId: z.string(), labStepId: z.string().nullable(), config: configSchema }).nullable(),
  prefs: z.object({
    splitPercent: num,
    speed: num,
    reducedMotion: z.enum(['system', 'on', 'off']),
    showAcceleration: z.boolean(),
    showContributions: z.boolean(),
  }),
});
const envelopeSchema = z.object({
  schemaVersion: z.number().int(),
  contentVersion: z.string(),
  savedAt: num,
  data: z.unknown(),
});

export type LoadStatus = 'ok' | 'empty' | 'unavailable' | 'corrupt' | 'obsolete';

export interface LoadResult {
  status: LoadStatus;
  data: PersistedData | null;
  message: string | null;
  /** Content version the data was saved under (may differ from current). */
  contentVersion: string | null;
}

/** Known schema migrations: schemaVersion n → n+1. */
const MIGRATIONS: Record<number, (data: unknown) => unknown> = {};

export function migrate(schemaVersion: number, data: unknown): unknown {
  let v = schemaVersion;
  let d = data;
  while (v < SCHEMA_VERSION) {
    const step = MIGRATIONS[v];
    if (!step) throw new Error(`No migration from schema ${v}`);
    d = step(d);
    v++;
  }
  return d;
}

export function parseEnvelope(raw: string): LoadResult {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { status: 'corrupt', data: null, message: 'Saved progress could not be read (not valid JSON).', contentVersion: null };
  }
  const env = envelopeSchema.safeParse(json);
  if (!env.success) return { status: 'corrupt', data: null, message: 'Saved progress has an unexpected format.', contentVersion: null };
  if (env.data.schemaVersion > SCHEMA_VERSION) {
    return { status: 'obsolete', data: null, message: 'Saved progress is from a newer version of this tutorial.', contentVersion: env.data.contentVersion };
  }
  let migrated: unknown;
  try {
    migrated = migrate(env.data.schemaVersion, env.data.data);
  } catch (e) {
    return { status: 'obsolete', data: null, message: String(e), contentVersion: env.data.contentVersion };
  }
  const parsed = dataSchema.safeParse(migrated);
  if (!parsed.success) {
    return { status: 'corrupt', data: null, message: 'Saved progress failed validation and was not loaded.', contentVersion: env.data.contentVersion };
  }
  return { status: 'ok', data: parsed.data as PersistedData, message: null, contentVersion: env.data.contentVersion };
}

export function serialize(data: PersistedData): string {
  return JSON.stringify({ schemaVersion: SCHEMA_VERSION, contentVersion: CONTENT_VERSION, savedAt: Date.now(), data });
}

function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    if (!s) return null;
    const probe = '__probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

export function loadFromStorage(): LoadResult {
  const s = storage();
  if (!s) return { status: 'unavailable', data: null, message: 'Browser storage is unavailable.', contentVersion: null };
  let raw: string | null;
  try {
    raw = s.getItem(STORAGE_KEY);
  } catch {
    return { status: 'unavailable', data: null, message: 'Browser storage is unavailable.', contentVersion: null };
  }
  if (raw === null) return { status: 'empty', data: null, message: null, contentVersion: null };
  return parseEnvelope(raw);
}

export type SaveStatus = 'saved' | 'trimmed' | 'failed' | 'unavailable';

/**
 * Save, trimming old non-evidence runs if the quota is exceeded.
 * Evidence runs (including those of passed gates) are never dropped.
 */
export function saveToStorage(data: PersistedData, protectedRunIds: Set<string>): { status: SaveStatus; data: PersistedData } {
  const s = storage();
  if (!s) return { status: 'unavailable', data };
  let current = data;
  let trimmed = false;
  for (let attempt = 0; attempt < 50; attempt++) {
    try {
      s.setItem(STORAGE_KEY, serialize(current));
      return { status: trimmed ? 'trimmed' : 'saved', data: current };
    } catch {
      const idx = current.runs.findIndex((r) => !protectedRunIds.has(r.id));
      if (idx === -1) return { status: 'failed', data: current };
      trimmed = true;
      current = { ...current, runs: current.runs.filter((_, i) => i !== idx), comparison: current.comparison.filter((id) => id !== current.runs[idx].id) };
    }
  }
  return { status: 'failed', data: current };
}

export function clearStorage(): void {
  try {
    globalThis.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Keep at most MAX_RUNS, dropping the oldest unprotected runs first. */
export function boundRuns<T extends { id: string }>(runs: T[], protectedIds: Set<string>, max = MAX_RUNS): T[] {
  let out = runs;
  while (out.length > max) {
    const idx = out.findIndex((r) => !protectedIds.has(r.id));
    if (idx === -1) break;
    out = out.filter((_, i) => i !== idx);
  }
  return out;
}
